import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { NewsletterTemplateState } from "./newsletter-template.state";
import { NewsletterTemplateModel } from "src/app/models/newsletter-template-model";
import { NewsletterTemplateService } from "src/app/services/newsletter-template.service";

const initialState: NewsletterTemplateState = {
    loading: false,
    loaded: false,
    success: false,
    errorMessage: null,
    newsletterTemplates: [],
    currentPage: 0,
    totalElements: 0
};

@Injectable({
    providedIn: 'root'
})
export class NewsletterTemplateComponentStore extends ComponentStore<NewsletterTemplateState> {

    constructor(private readonly newsletterTemplateService: NewsletterTemplateService) {
        super(initialState);
    }

    readonly newsletterTemplate$: Observable<NewsletterTemplateModel[]> = this.select(state => state.newsletterTemplates);
    readonly currentPage$: Observable<number> = this.select(state => state.currentPage);
    readonly totalElements$: Observable<number> = this.select(state => state.totalElements);
    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);

    readonly viewModel$ = this.select(
        this.newsletterTemplate$,
        this.currentPage$,
        this.totalElements$,
        this.loaded$,
        this.loading$,
        this.success$,
        this.errorMessage$,
        (newsletterTemplates, currentPage, totalElements, loaded, loading, success, errorMessage) => ({
            newsletterTemplates, currentPage, totalElements, loaded, loading, success, errorMessage
        })
    );

    readonly setInitial = this.updater((_: NewsletterTemplateState) => {
        return {
            ...initialState
        };
    });

    readonly setLoading = this.updater((state: NewsletterTemplateState) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    });

    readonly setLoaded = this.updater((state: NewsletterTemplateState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });


    readonly updateNewsletterTemplateState = this.updater((state: NewsletterTemplateState, value: {
        content: NewsletterTemplateModel[],
        currentPage: number,
        totalElements: number
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            newsletterTemplates: value.content,
            currentPage: value.currentPage,
            totalElements: value.totalElements
        };
    });

    readonly setError = this.updater((state: NewsletterTemplateState, value: {
        errorMessage: String
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly getNewsletterTemplates = this.effect((params$: Observable<{ companyId: string, currentPage: number, pageSize: number }>) => {
        this.setLoading();
        return params$.pipe(
            switchMap((params => this.newsletterTemplateService.getNewsletterTemplates(params.companyId, params.currentPage, params.pageSize).pipe(
                map((page) => {
                    this.updateNewsletterTemplateState({ content: page.content, currentPage: page.number, totalElements: page.totalElements })
                    this.setLoaded();
                }),
                catchError(async (error) => {
                    this.setError({ errorMessage: error })
                    this.setLoaded()
                })
            ))
            )
        );
    });
}